/**
 * PureOne Apple-Style Scroll Animations
 * Implements smooth scroll-triggered animations throughout the page
 */

class PureOneAnimations {
  constructor() {
    this.init();
    this.setupIntersectionObservers();
    this.setupParallax();
    this.setupSmoothScroll();
    this.setupMicroInteractions();
    // Scroll progress bar removed per user request
    // this.setupScrollProgress();
    this.setupImageZoom();
    this.setupCountUpAnimations();
    this.setupRippleEffects();
    this.setupHowItWorksAnimations();
  }

  init() {
    // Add animation classes to elements on page load
    document.documentElement.classList.add('animations-enabled');

    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.prefersReducedMotion) {
      document.documentElement.classList.add('reduced-motion');
      return;
    }

    // Initialize animations
    this.animateOnLoad();
  }

  animateOnLoad() {
    // Animate hero elements on page load
    const heroElements = document.querySelectorAll('.apple-fade-in, .apple-fade-in-delay');
    heroElements.forEach((el, index) => {
      el.style.animationDelay = `${index * 0.1}s`;
      el.classList.add('animate');
    });
  }

  setupIntersectionObservers() {
    if (this.prefersReducedMotion) return;

    // Options for different animation triggers
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    // Create observer for fade-in animations
    const fadeInObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');

          // Stagger child animations if present
          const children = entry.target.querySelectorAll('.animate-child');
          children.forEach((child, index) => {
            setTimeout(() => {
              child.classList.add('in-view');
            }, index * 100);
          });
        }
      });
    }, observerOptions);

    // Create observer for scale animations
    const scaleObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('scaled-in');
        }
      });
    }, { threshold: 0.3 });

    // Create observer for slide animations
    const slideObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const direction = entry.target.dataset.slideDirection || 'up';
          entry.target.classList.add(`slide-in-${direction}`);
        }
      });
    }, observerOptions);

    // Apply observers to elements
    document.querySelectorAll('.apple-animate-fade').forEach(el => {
      fadeInObserver.observe(el);
    });

    document.querySelectorAll('.apple-animate-scale').forEach(el => {
      scaleObserver.observe(el);
    });

    document.querySelectorAll('.apple-animate-slide').forEach(el => {
      slideObserver.observe(el);
    });

    // Special animations for feature showcases
    const featureObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Animate the feature image
          const image = entry.target.querySelector('.feature-visual');
          if (image) {
            image.classList.add('reveal');
          }

          // Animate the content
          const content = entry.target.querySelector('.feature-content');
          if (content) {
            setTimeout(() => {
              content.classList.add('reveal');
            }, 200);
          }

          // Animate stats/numbers
          const stats = entry.target.querySelectorAll('.stat-number');
          stats.forEach(stat => {
            this.animateNumber(stat);
          });
        }
      });
    }, { threshold: 0.3 });

    document.querySelectorAll('.feature-section').forEach(el => {
      featureObserver.observe(el);
    });

    // Progress bars animation
    const progressObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const bars = entry.target.querySelectorAll('.progress-bar-fill');
          bars.forEach((bar, index) => {
            setTimeout(() => {
              const width = bar.dataset.width || '100';
              bar.style.width = `${width}%`;
            }, index * 150);
          });
        }
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('.comparison-table-apple').forEach(el => {
      progressObserver.observe(el);
    });
  }

  setupParallax() {
    if (this.prefersReducedMotion) return;

    const parallaxElements = document.querySelectorAll('[data-parallax]');

    if (parallaxElements.length === 0) return;

    let ticking = false;

    const updateParallax = () => {
      const scrolled = window.pageYOffset;
      const windowHeight = window.innerHeight;

      parallaxElements.forEach(element => {
        const rect = element.getBoundingClientRect();
        const speed = parseFloat(element.dataset.parallax) || 0.5;

        // Only animate if element is in viewport
        if (rect.bottom >= 0 && rect.top <= windowHeight) {
          const yPos = -(scrolled * speed);
          element.style.transform = `translate3d(0, ${yPos}px, 0)`;
        }
      });

      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });
  }

  setupSmoothScroll() {
    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');
        if (href === '#') return;

        const target = document.querySelector(href);
        if (!target) return;

        e.preventDefault();

        const offset = 100; // Adjust for fixed header
        const targetPosition = target.offsetTop - offset;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });

        // Update URL without jumping
        history.pushState(null, null, href);
      });
    });
  }

  setupMicroInteractions() {
    if (this.prefersReducedMotion) return;

    // Button hover effects
    document.querySelectorAll('.apple-button-primary, .apple-button-ghost').forEach(button => {
      button.addEventListener('mouseenter', (e) => {
        const ripple = document.createElement('span');
        ripple.classList.add('button-ripple');
        button.appendChild(ripple);

        setTimeout(() => {
          ripple.remove();
        }, 600);
      });
    });

    // Card hover effects
    document.querySelectorAll('.hover-lift').forEach(card => {
      card.addEventListener('mouseenter', () => {
        card.style.transform = 'translateY(-4px)';
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = 'translateY(0)';
      });
    });

    // Image reveal on hover
    document.querySelectorAll('.image-hover-zoom').forEach(container => {
      const image = container.querySelector('img');
      if (!image) return;

      container.addEventListener('mouseenter', () => {
        image.style.transform = 'scale(1.05)';
      });

      container.addEventListener('mouseleave', () => {
        image.style.transform = 'scale(1)';
      });
    });

    // Magnetic buttons
    document.querySelectorAll('.magnetic-button').forEach(button => {
      button.addEventListener('mousemove', (e) => {
        const rect = button.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;

        button.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
      });

      button.addEventListener('mouseleave', () => {
        button.style.transform = 'translate(0, 0)';
      });
    });
  }

  animateNumber(element) {
    const target = parseFloat(element.dataset.target || element.textContent);
    const duration = parseInt(element.dataset.duration) || 2000;
    const prefix = element.dataset.prefix || '';
    const suffix = element.dataset.suffix || '';
    const decimals = parseInt(element.dataset.decimals) || 0;
    const start = 0;
    const startTime = Date.now();

    // Easing function for smooth animation
    const easeOutQuart = t => 1 - Math.pow(1 - t, 4);

    const updateNumber = () => {
      const progress = Math.min((Date.now() - startTime) / duration, 1);
      const current = start + (target - start) * easeOutQuart(progress);

      if (decimals > 0) {
        element.textContent = prefix + current.toFixed(decimals) + suffix;
      } else {
        element.textContent = prefix + Math.floor(current).toLocaleString() + suffix;
      }

      if (progress < 1) {
        requestAnimationFrame(updateNumber);
      } else {
        // Ensure final value is exact
        if (decimals > 0) {
          element.textContent = prefix + target.toFixed(decimals) + suffix;
        } else {
          element.textContent = prefix + Math.floor(target).toLocaleString() + suffix;
        }
      }
    };

    updateNumber();
  }

  // REMOVED: Scroll progress bar at top of page
  // Uncomment this function and the constructor call above if you want to restore it
  /*
  setupScrollProgress() {
    // Create and add scroll progress indicator
    const progressBar = document.createElement('div');
    progressBar.className = 'scroll-progress-bar';
    progressBar.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      height: 3px;
      background: linear-gradient(90deg, var(--brand-primary), var(--brand-secondary));
      z-index: 9999;
      width: 0;
      transition: width 0.1s ease;
    `;
    document.body.appendChild(progressBar);

    const updateProgress = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPosition = window.pageYOffset;
      const progress = (scrollPosition / scrollHeight) * 100;
      progressBar.style.width = `${progress}%`;
    };

    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    updateProgress();
  }
  */

  setupImageZoom() {
    // Add zoom functionality to product images
    const zoomableImages = document.querySelectorAll('[data-zoomable], .product-image img, .gallery-image');

    zoomableImages.forEach(img => {
      img.style.cursor = 'zoom-in';

      img.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Create overlay
        const overlay = document.createElement('div');
        overlay.className = 'image-zoom-overlay';
        overlay.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          z-index: 9998;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: zoom-out;
          opacity: 0;
          transition: opacity 0.3s ease;
          backdrop-filter: blur(10px);
        `;

        // Create zoomed image container
        const imageContainer = document.createElement('div');
        imageContainer.style.cssText = `
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
        `;

        // Create zoomed image
        const zoomedImg = img.cloneNode();
        zoomedImg.style.cssText = `
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
          transform: scale(0.8);
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        `;

        // Add close button
        const closeBtn = document.createElement('button');
        closeBtn.innerHTML = '×';
        closeBtn.style.cssText = `
          position: absolute;
          top: -40px;
          right: 0;
          width: 40px;
          height: 40px;
          background: transparent;
          border: 2px solid white;
          border-radius: 50%;
          color: white;
          font-size: 24px;
          cursor: pointer;
          transition: transform 0.3s ease;
        `;
        closeBtn.addEventListener('mouseenter', () => {
          closeBtn.style.transform = 'rotate(90deg)';
        });
        closeBtn.addEventListener('mouseleave', () => {
          closeBtn.style.transform = 'rotate(0)';
        });

        imageContainer.appendChild(zoomedImg);
        imageContainer.appendChild(closeBtn);
        overlay.appendChild(imageContainer);
        document.body.appendChild(overlay);
        document.body.style.overflow = 'hidden';

        // Trigger animation
        requestAnimationFrame(() => {
          overlay.style.opacity = '1';
          zoomedImg.style.transform = 'scale(1)';
        });

        // Close handlers
        const closeOverlay = () => {
          overlay.style.opacity = '0';
          zoomedImg.style.transform = 'scale(0.8)';
          setTimeout(() => {
            overlay.remove();
            document.body.style.overflow = '';
          }, 300);
        };

        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) closeOverlay();
        });
        closeBtn.addEventListener('click', closeOverlay);

        // ESC key to close
        const handleEsc = (e) => {
          if (e.key === 'Escape') {
            closeOverlay();
            document.removeEventListener('keydown', handleEsc);
          }
        };
        document.addEventListener('keydown', handleEsc);
      });
    });
  }

  setupCountUpAnimations() {
    // Advanced count-up animations for stats
    const countUpElements = document.querySelectorAll('[data-count-up]');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains('counted')) {
          entry.target.classList.add('counted');
          this.animateNumber(entry.target);
        }
      });
    }, { threshold: 0.5 });

    countUpElements.forEach(el => observer.observe(el));
  }

  setupRippleEffects() {
    // Add ripple effect to buttons
    const buttons = document.querySelectorAll('button:not(.no-ripple), .btn:not(.no-ripple), .apple-button-primary, .apple-button-ghost');

    buttons.forEach(button => {
      button.style.position = 'relative';
      button.style.overflow = 'hidden';

      button.addEventListener('click', function(e) {
        // Remove any existing ripples
        const existingRipple = this.querySelector('.ripple-effect');
        if (existingRipple) existingRipple.remove();

        const ripple = document.createElement('span');
        ripple.className = 'ripple-effect';

        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;

        ripple.style.cssText = `
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          left: ${x}px;
          top: ${y}px;
          background: radial-gradient(circle, rgba(255,255,255,0.5) 0%, transparent 70%);
          border-radius: 50%;
          transform: scale(0);
          animation: ripple-animation 0.6s ease-out;
          pointer-events: none;
        `;

        this.appendChild(ripple);
        setTimeout(() => ripple.remove(), 600);
      });
    });

    // Add ripple animation styles if not exists
    if (!document.querySelector('#ripple-animation-styles')) {
      const style = document.createElement('style');
      style.id = 'ripple-animation-styles';
      style.textContent = `
        @keyframes ripple-animation {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // Utility function to add animation classes to sections
  static addAnimationClasses() {
    // Add animation classes to existing sections
    const sections = {
      '.pureone-hero': 'apple-animate-fade',
      '.feature-section': 'apple-animate-slide',
      '.lifestyle-scenario': 'apple-animate-fade animate-child',
      '.science-card': 'apple-animate-scale',
      '.comparison-table-apple': 'apple-animate-fade',
      '.testimonial-card-apple': 'apple-animate-fade animate-child',
      '.spec-row': 'apple-animate-slide',
      '.faq-item': 'apple-animate-fade animate-child',
      '.purchase-option': 'apple-animate-scale',
      '.benefit-card': 'hover-lift',
      '.product-image': 'image-hover-zoom',
      '.cta-button': 'magnetic-button'
    };

    Object.entries(sections).forEach(([selector, className]) => {
      document.querySelectorAll(selector).forEach(el => {
        className.split(' ').forEach(cls => {
          el.classList.add(cls);
        });
      });
    });

    // Add data attributes for special animations
    document.querySelectorAll('.hero-background').forEach(el => {
      el.setAttribute('data-parallax', '0.5');
    });

    document.querySelectorAll('.floating-element').forEach(el => {
      el.setAttribute('data-parallax', '0.3');
    });

    // Add count-up attributes to stat numbers
    document.querySelectorAll('.stat-number').forEach(el => {
      const text = el.textContent.trim();

      // Skip count-up animation for text-based stats like "24/7 Support" or "24 hr"
      // Check if text contains "/" or has space followed by letters (like "24 hr")
      if (text.includes('/') || text.match(/\s+[a-zA-Z]/)) {
        // This is a text stat, not a number stat - skip animation
        return;
      }

      const value = text.replace(/[^0-9.]/g, '');
      if (value) {
        el.setAttribute('data-count-up', value);
        el.setAttribute('data-duration', '2500');

        // Check for special formatting
        if (text.includes('%')) {
          el.setAttribute('data-suffix', '%');
        } else if (text.includes('+')) {
          el.setAttribute('data-suffix', '+');
        } else if (text.includes('ppb')) {
          el.setAttribute('data-suffix', ' ppb');
        }
      }
    });

    // Mark gallery images as zoomable
    document.querySelectorAll('.product-gallery img, .feature-visual img').forEach(el => {
      el.setAttribute('data-zoomable', 'true');
    });
  }

  setupHowItWorksAnimations() {
    if (this.prefersReducedMotion) return;

    // Find all How It Works icon circles
    const iconCircles = document.querySelectorAll('.how-it-works-icon-circle');
    if (iconCircles.length === 0) return;

    // Set initial state - hidden and scaled down
    iconCircles.forEach(circle => {
      circle.style.opacity = '0';
      circle.style.transform = 'scale(0.5) rotate(-10deg)';
      circle.style.transition = 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)';
    });

    // Create intersection observer for the section
    const howItWorksObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const icons = entry.target.querySelectorAll('.how-it-works-icon-circle');

          // Animate each icon with a sequential delay
          icons.forEach((icon, index) => {
            setTimeout(() => {
              icon.style.opacity = '1';
              icon.style.transform = 'scale(1) rotate(0deg)';

              // Add a subtle pulse animation after the main animation
              setTimeout(() => {
                icon.style.transform = 'scale(1.05) rotate(0deg)';
                setTimeout(() => {
                  icon.style.transform = 'scale(1) rotate(0deg)';
                }, 150);
              }, 600);

              // Animate the icon SVG inside with a slight delay
              const iconSvg = icon.querySelector('.how-it-works-icon svg');
              if (iconSvg) {
                iconSvg.style.opacity = '0';
                iconSvg.style.transform = 'scale(0.8)';
                iconSvg.style.transition = 'opacity 0.4s ease, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';

                setTimeout(() => {
                  iconSvg.style.opacity = '1';
                  iconSvg.style.transform = 'scale(1)';
                }, 200);
              }
            }, index * 300); // 300ms delay between each icon (1, 2, 3, 4 sequence)
          });

          // Unobserve after animation to prevent re-triggering
          howItWorksObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.2, // Trigger when 20% of the section is visible
      rootMargin: '0px 0px -50px 0px' // Slight offset to trigger a bit before center
    });

    // Observe the How It Works section
    const howItWorksSection = document.querySelector('.how-it-works-section');
    if (howItWorksSection) {
      howItWorksObserver.observe(howItWorksSection);
    }
  }
}

// Initialize animations when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    PureOneAnimations.addAnimationClasses();
    new PureOneAnimations();
  });
} else {
  PureOneAnimations.addAnimationClasses();
  new PureOneAnimations();
}

// Export for use in other modules
export default PureOneAnimations;